/**
 * Build the unified ordered list of social links for the candidate profile
 * header. Shared by features/candidate/components/profile/header.tsx and
 * features/arbitrator/components/review/header.tsx.
 *
 * Behavior:
 *   - Returns `[]` when `profile` is null / undefined.
 *   - Allocates a `Set<string>` seeded with the canonical first-class network
 *     ids ('linkedin', 'github', 'twitter') BEFORE iterating
 *     `profile.social_profiles`. The Set guarantees the explicit
 *     `linkedin_url` / `github_username` / `twitter_url` shortcuts win over
 *     duplicate entries in `social_profiles`.
 *   - Iterates `profile.social_profiles`, lower-cases the `network` key, skips
 *     entries already in `seen` or missing a `url`, applies `toSocialNetwork`
 *     inline to map the raw key onto the strongly-typed domain enum, and
 *     pushes `{ url, network, label }` (label is the raw network key from the
 *     profile entry, preserving the existing header shape).
 *
 * `url` is normalized through `ensureAbsoluteUrl` so consumers get an
 * absolute URL safe for `<Link href />`.
 */

import type { CandidateProfile } from "@/lib/api/types";
import { toSocialNetwork } from "@/features/candidate/lib/socials";
import { ensureAbsoluteUrl } from "@/lib/url/ensure-absolute-url";

export interface SocialLink {
  url: string;
  network: ReturnType<typeof toSocialNetwork>;
  label: string;
}

export function buildSocialLinks(
  profile: CandidateProfile | null | undefined,
): SocialLink[] {
  if (!profile) return [];

  const links: SocialLink[] = [];
  // Seed the seen-Set BEFORE the loop so the explicit linkedin_url /
  // github_username / twitter_url shortcuts dedupe against entries that come
  // through profile.social_profiles with the same network key.
  const seen = new Set<string>(["linkedin", "github", "twitter"]);

  if (profile.linkedin_url) {
    links.push({
      url: ensureAbsoluteUrl(profile.linkedin_url),
      network: "linkedin.com",
      label: "Social",
    });
  }
  if (profile.github_username) {
    links.push({
      url: `https://github.com/${profile.github_username}`,
      network: "github.com",
      label: "GitHub",
    });
  }
  if (profile.twitter_url) {
    links.push({
      url: ensureAbsoluteUrl(profile.twitter_url),
      network: "twitter.com",
      label: "Twitter",
    });
  }

  for (const raw of profile.social_profiles ?? []) {
    const sp = raw as Record<string, unknown>;
    const net = typeof sp.network === "string" ? sp.network.toLowerCase() : "";
    const url = typeof sp.url === "string" ? sp.url : "";
    if (!net || !url) continue;
    if (seen.has(net)) continue;
    seen.add(net);
    links.push({
      url: ensureAbsoluteUrl(url),
      network: toSocialNetwork(net),
      label: net,
    });
  }

  return links;
}
